import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { preFilterService, KeywordLists, KeywordType, TestResult } from '../services/preFilterService';

const PreFilterSettings: React.FC = () => {
  const [pfKeywords, setPfKeywords] = useState<KeywordLists | null>(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfNewKeyword, setPfNewKeyword] = useState('');
  const [pfNewType, setPfNewType] = useState<KeywordType>('requestKeywords');
  const [pfTestInput, setPfTestInput] = useState('');
  const [pfTestResult, setPfTestResult] = useState<TestResult | null>(null);
  const [pfTestLoading, setPfTestLoading] = useState(false);

  const loadPfKeywords = async () => {
    try {
      setPfLoading(true);
      const data = await preFilterService.getKeywords();
      setPfKeywords(data);
    } catch {
      toast.error('Failed to load pre-filter keywords');
    } finally {
      setPfLoading(false);
    }
  };

  useEffect(() => {
    loadPfKeywords();
  }, []);

  const handlePfAdd = async () => {
    const kw = pfNewKeyword.trim();
    if (!kw) return;
    try {
      await preFilterService.addKeyword(pfNewType, kw);
      setPfNewKeyword('');
      toast.success(`Added "${kw}" to ${pfNewType}`);
      await loadPfKeywords();
    } catch {
      toast.error('Failed to add keyword');
    }
  };

  const handlePfRemove = async (type: KeywordType, keyword: string) => {
    try {
      await preFilterService.removeKeyword(type, keyword);
      toast.success(`Removed "${keyword}"`);
      await loadPfKeywords();
    } catch {
      toast.error('Failed to remove keyword');
    }
  };

  const handlePfTest = async () => {
    if (!pfTestInput.trim()) return;
    try {
      setPfTestLoading(true);
      const result = await preFilterService.testMessage(pfTestInput);
      setPfTestResult(result);
    } catch {
      toast.error('Failed to test message');
    } finally {
      setPfTestLoading(false);
    }
  };

  if (pfLoading && !pfKeywords) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Pre-filter Keywords</Typography>

      <Grid container spacing={3}>
        {/* Add keyword */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Add Keyword</Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={pfNewType}
                  label="Category"
                  onChange={(e) => setPfNewType(e.target.value as KeywordType)}
                >
                  <MenuItem value="requestKeywords">Request keywords</MenuItem>
                  <MenuItem value="offerKeywords">Offer keywords</MenuItem>
                  <MenuItem value="locations">Locations</MenuItem>
                  <MenuItem value="carTypes">Car types</MenuItem>
                  <MenuItem value="excludeKeywords">Exclude keywords</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Keyword"
                value={pfNewKeyword}
                onChange={(e) => setPfNewKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePfAdd()}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={handlePfAdd} sx={{ height: 56 }}>
                Add
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Keyword lists */}
        {pfKeywords && ([
          { key: 'requestKeywords' as KeywordType, label: 'Request keywords', color: 'primary' },
          { key: 'offerKeywords' as KeywordType, label: 'Offer keywords', color: 'success' },
          { key: 'locations' as KeywordType, label: 'Locations', color: 'info' },
          { key: 'carTypes' as KeywordType, label: 'Car types', color: 'secondary' },
          { key: 'excludeKeywords' as KeywordType, label: 'Exclude keywords', color: 'error' },
        ] as const).map(({ key, label, color }) => (
          <Grid item xs={12} md={6} key={key}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {label} ({pfKeywords[key].length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {pfKeywords[key].length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None</Typography>
                ) : pfKeywords[key].map((kw) => (
                  <Chip
                    key={kw}
                    label={kw}
                    color={color as any}
                    variant="outlined"
                    size="small"
                    onDelete={() => handlePfRemove(key, kw)}
                    deleteIcon={<DeleteIcon />}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Test message */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Test a message</Typography>
            <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
              <TextField
                label="Message text"
                multiline
                minRows={2}
                value={pfTestInput}
                onChange={(e) => setPfTestInput(e.target.value)}
                sx={{ flex: 1, minWidth: 260 }}
              />
              <Button
                variant="outlined"
                onClick={handlePfTest}
                disabled={pfTestLoading || !pfTestInput.trim()}
                sx={{ height: 56 }}
              >
                {pfTestLoading ? 'Testing…' : 'Test'}
              </Button>
            </Box>
            {pfTestResult && (
              <Alert
                severity={pfTestResult.preFilterResult.isRideRelated ? 'success' : 'info'}
                sx={{ mt: 2 }}
              >
                <strong>{pfTestResult.preFilterResult.isRideRelated ? 'Ride-related' : 'Not ride-related'}</strong>
                {' — '}{pfTestResult.preFilterResult.type} · confidence {Math.round(pfTestResult.preFilterResult.confidence * 100)}%
                {' · '}{pfTestResult.wouldSendToAI ? 'would send to AI' : 'pre-filter handles it (no AI cost)'}
                <br />
                <Typography variant="caption">{pfTestResult.preFilterResult.reason}</Typography>
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PreFilterSettings;
